import React from "react";

import _ from "lodash";
import * as d3 from "d3-interpolate";
import mapbox from "mapbox-gl";
import { bbox as turfBBox, area as turfArea } from "@turf/turf";
import { toast } from "react-toastify";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
//@ts-ignore
import * as FileSaver from "file-saver";

import { Column, Row } from "@src/components/Flex";
import Dialog from "@components/Dialog";
import Spinner from "@components/Spinner";
import GeoMap, { Source, Layer, GeoJSONSource } from "@components/Map";
import Input from "@components/Input";
import Button from "@components/Button";
import { ReactComponent as ExpandLess } from "@assets/icons/expand_less.svg";
import { ReactComponent as ExpandMore } from "@assets/icons/expand_more.svg";
import { fetchPrescription, type PrescriptionLayersOption } from "@services/mowerService";
import { api } from "@services/api";
import { track, EVENTS } from "@src/utils/analytics";
import { useFormatArea, useLang } from "@src/hooks/useLang";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { useStores } from "@stores";
import UploadToFilesDialog from "@screens/PrescriptionScreen/UploadToFilesDialog";
import LayerPicker from "@screens/PrescriptionScreen/LayerPicker";
import { buildZoneRates, getRate, type Zone } from "@screens/PrescriptionScreen/rates";
import { getJohnDeereStatus } from "@services/johnDeereService";
import { getPrescriptionSources, type TPrescriptionSources } from "@services/fieldsService";
import Text from "@components/Text";

type State<S> = [S, React.Dispatch<React.SetStateAction<S>>];

export const cn = (...classes: string[]): string => {
  return _.join(_.filter(_.uniq(classes), (e) => !_.isEmpty(e)), " ");
};

const LoadingLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;
  background: rgba(256, 256, 256, 0.5);
  z-index: 20;
`;

const PaywallLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 1.5rem;
`;

const PaywallCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 420px;
  padding: 2rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
`;

const StyleScreen = styled.div`
  height: 100vh;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: var(--vh-full, 100vh);
  }
`;

const StyleModalWindow = styled.div`
  padding: 0.75rem;
  background-color: #fff;
  border-radius: 0.25rem;
  width: 24rem;
  z-index: 10;
  top: 0.5rem;
  left: 0.5rem;
  position: absolute;
`;

const StyleModalHeader = styled.div`
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
`;

const StyleModalHeaderText = styled.div`
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 600;
`;

const StyleModalContainer = styled.div<{ hidden: boolean }>`
  padding-top: 0.75rem;
  display: ${({ hidden }) => (hidden) ? "none" : "block"};
`;

const StyleCountSelect = styled.div`
  --tw-shadow: inset 0 2px 4px 0 rgba(0,0,0,.05);
  --tw-shadow-colored: inset 0 2px 4px 0;
  box-shadow: 0 0 #0000,0 0 #0000,var(--tw-shadow);
  box-shadow: var(--tw-ring-offset-shadow,0 0 #0000),var(--tw-ring-shadow,0 0 #0000),var(--tw-shadow);
  color: rgb(107 114 128);
  font-weight: 500;
  font-size: .875rem;
  line-height: 1.25rem;
  text-align: center;
  padding-bottom: 0.25rem;
  padding-top: 0.25rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  background-color: rgb(229 231 235);
  border-radius: 0.25rem;
  gap: 0.25rem;
  display: flex;
`;

const StyleSelect = styled.div<{ selected: boolean }>`
  color: #000;
  border-radius: 0.25rem;
  padding-bottom: 0.25rem;
  padding-top: 0.25rem;
  padding-left: 1.75rem;
  padding-right: 1.75rem;
  -webkit-user-select: none;
  user-select: none;
  background-color: ${({ selected }) => (selected) ? "#fff" : "transparent"}
`;

const StyleRateInputContainer = styled.div`
  padding: 1rem 0;
`;

const StyleOptionsHeader = styled.div`
  -webkit-columns: 3;
  column-count: 3;
`;

const StyleOptionsHeaderCell = styled.div`
  font-weight: 600;
  padding-left: 0.5rem;
  padding-bottom: 0.25rem;
`;

const StyleOptionsContainer = styled.div`
  margin-right: 0.25rem;
  max-height: 50vh;
  overflow-y: auto;
`;

const StyleOptionsRow = styled.div`
  padding-left: 0.5rem;
  padding-bottom: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;

  :first {
    border-top-width: 0;
  }
`;

const StyleOptionsRowContainer = styled.div<{ borderColor: string }>`
  display: grid;
  grid-template-columns: repeat(3,minmax(0,1fr));
  border-right: 4px solid ${({ borderColor }) => borderColor};
`;

const StyleSubtext = styled.span`
  color: rgb(156 163 175);
  font-size: .875rem;
  line-height: 1.25rem;
`;

const StyleOptionsInputWrapper = styled.div`
  padding-top: 0.25rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  grid-column: span 2/span 2;
`;

const StyleActions = styled.div`
  position: absolute;
  display: flex;
  gap: 0.5rem;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 10;
  padding: 0.25rem;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  -webkit-user-select: none;
  user-select: none;

  & > button {
    min-height: 44px;
  }
`;



const pallet = d3.interpolateRgbBasisClosed([
  "rgba(9, 52, 12, 1)",
  "rgba(17, 94, 13, 1)",
  "rgba(54, 155, 9, 1)",
  "rgba(147, 212, 11, 1)",
  "rgba(254, 246, 32, 1)",
  "rgba(237, 142, 7, 1)",
  "rgba(170, 57, 15, 1)",
  "rgba(82, 18, 0, 1)",
]);


const interpolate = (n: number, max: number) => {
  return pallet(1 / (max + 1) * n);
};

// useFieldID is an effect that returns field id from the page url.
const useFieldID = (): [string, string | null] => {
  const params = useParams();
  const location = useLocation();
  const search = new URLSearchParams(location.search);

  const field_id = params.field_id;
  if (!field_id) {
    throw new Error("Invalid page address");
  }
  return [field_id, location.state.field_name ?? search.get("field_name")];
};

const CountSelect: React.FC<{ countState: State<number> }> = ({ countState }) => {
  const lang = useLang();

  const options = [3, 5, 7, 9];
  const [count, setCount] = countState;
  return (
    <div>
      <div style={{ paddingLeft: "0.5rem" }}>
        <StyleSubtext>{lang.prescription.zones}</StyleSubtext>
      </div>
      <StyleCountSelect>
        {
          options.map((option) => {
            const selected = (count === option);
            return (
              <div
                key={option}
                style={{ flexGrow: 1, cursor: "pointer" }}
                onClick={() => setCount(option)}
              >
                <StyleSelect selected={selected}>{option}</StyleSelect>
              </div>
            );
          })
        }
      </StyleCountSelect>
    </div>
  );
};

/** Query for the single-season export (GET). rates[i] is Zone i + 1 — never reordered. */
const downloadQuery = (
  field_name: string | null,
  rates: number[],
  format: string,
  seasonId: string | undefined,
  zoneCount: number,
): Record<string, string> => ({
  format,
  rates: rates.join(","),
  zones: zoneCount.toString(),
  ...(seasonId ? { seasonId } : {}),
  ...(field_name ? { field_name: field_name.replaceAll(" ", "_") } : {}),
});

const DownloadDialog: React.FC<{
  field_id: string,
  field_name: string | null,
  standard: number,
  zones: Zone[],
  zoneCount: number,
  seasonId: string | undefined,
  combined?: PrescriptionLayersOption,
  visible: boolean,
  onClose: () => void;
}> = ({ field_id, field_name, standard, zones, zoneCount, seasonId, combined, visible, onClose }) => {
  const lang = useLang();

  const rates = buildZoneRates(zones, standard);

  const clickDownload = (format: string) => {
    const name = (field_name ?? field_id).replaceAll(" ", "_");
    track(EVENTS.PRESCRIPTION_EXPORTED, {
      format,
      zoneCount,
      combined: Boolean(combined && combined.layers.length > 0),
    });

    // Multi-layer prescriptions are exported via a JSON POST (the layers spec
    // doesn't fit cleanly in a query string); single-layer keeps the GET.
    const endpoint = `/api/v1/fields/${field_id}/prescription/export`;
    const request =
      combined && combined.layers.length > 0
        ? api.postBlob(endpoint, {
            layers: combined.layers,
            surface: combined.surface,
            zones: zoneCount,
            format,
            rates,
            field_name: field_name ?? undefined,
          })
        : api.getBlob(endpoint, downloadQuery(field_name, rates, format, seasonId, zoneCount));

    request
      .then((blob) => {
        FileSaver.saveAs(blob, `prescription_${name}.${format}.zip`);
        onClose();
      })
      .catch((e) => {
        // getBlob/postBlob never toast; show the server's reason when it gave one.
        console.error(e);
        toast.error(e?.message || lang.fields.tracksExportFailed);
      });
  };


  return (
    <Dialog
      title={lang.prescription.download}
      visible={visible}
      onClose={onClose}
      style={{ maxWidth: 500 }}
    >
      <Column
        justifyContent="space-between"
        crossAxisSize="max"
        mainAxisSize="stretch">
        <Row alignItems="center" style={{ marginTop: 16, gap: 32 }}>
          <Button
            style={{ minHeight: 42 }}
            onClick={() => clickDownload("shp")}>
            Shapefile
          </Button>
          <Button
            style={{ minHeight: 42 }}
            onClick={() => clickDownload("deere")}>
            John Deere
          </Button>
        </Row>
      </Column>
    </Dialog>
  );
}

const StandardRateInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const lang = useLang();

  return (
    <StyleRateInputContainer>
      <div style={{ paddingLeft: "0.5rem" }}>
        <StyleSubtext>{lang.prescription.standardRate} </StyleSubtext>
      </div>
      <Input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
      />
    </StyleRateInputContainer>
  );
};

const Options: React.FC<{
  zones: Zone[];
  standard: number;
  setRate: (id: number, rate: string) => void;
}> = ({ zones, standard, setRate }) => {
  const lang = useLang();
  const formatArea = useFormatArea();

  return (
    <StyleOptionsContainer>
      <StyleOptionsHeader>
        <StyleOptionsHeaderCell>{lang.prescription.zone}</StyleOptionsHeaderCell>
        <StyleOptionsHeaderCell>{lang.prescription.rate}</StyleOptionsHeaderCell>
      </StyleOptionsHeader>
      {
        zones.map((zone) => {
          const [, raw_rate] = getRate(zone, zones.length, standard);

          return <StyleOptionsRow key={zone.id}>
            <StyleOptionsRowContainer borderColor={zone.color}>
              <div>
                <div>{lang.prescription.zone} {zone.id}</div>
                <StyleSubtext>
                  {formatArea(zone.area / 10000)}
                </StyleSubtext>
              </div>
              <StyleOptionsInputWrapper>
                <Input
                  type="text"
                  value={raw_rate}
                  onChange={(e) => {
                    setRate(zone.id, e.target.value);
                  }}
                />
              </StyleOptionsInputWrapper>
            </StyleOptionsRowContainer>
          </StyleOptionsRow>
        })
      }
    </StyleOptionsContainer>
  );
};


const Prescription: React.FC = observer(() => {
  const [field_id, field_name] = useFieldID();
  const navigate = useNavigate();
  const lang = useLang();
  const { seasonStore, accountStore } = useStores();
  const seasonId = seasonStore.currentSeasonId;
  const isPaid = accountStore.subscriptionPaid;

  const [loading, setLoading] = React.useState(true);
  // Number of the zones
  const [count, setCount] = React.useState<number>(3);
  // Standard rate
  const [standard, setStandard] = React.useState<number>(100);
  // Raw text value of standard rate
  const [rawStandard, setRawStandard] = React.useState<string>(standard.toString());
  // Visiblity of DownloadDialog
  const [isExportDialogVisible, setExportDialogVisible] = React.useState<boolean>(false);
  const [isUploadFilesDialogVisible, setUploadFilesDialogVisible] = React.useState<boolean>(false);
  const [connectPromptVisible, setConnectPromptVisible] = React.useState<boolean>(false);
  // Whether a John Deere account is connected — gates the "Upload to JD files"
  // button (it only appears when connected).
  const [jdConnected, setJdConnected] = React.useState<boolean>(false);

  // Check the John Deere connection once on mount so we know whether to show
  // the upload button at all.
  React.useEffect(() => {
    let cancelled = false;
    getJohnDeereStatus()
      .then((s) => {
        if (!cancelled) setJdConnected(s.connected);
      })
      .catch(() => {
        if (!cancelled) setJdConnected(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleUploadToDeere = () => {
    getJohnDeereStatus()
      .then((s) => {
        setJdConnected(s.connected);
        if (s.connected) {
          setUploadFilesDialogVisible(true);
        } else {
          setConnectPromptVisible(true);
        }
      })
      .catch(() => {
        setConnectPromptVisible(true);
      });
  };

  const [hidden, setHidden] = React.useState<boolean>(false);
  const [map, setMap] = React.useState<mapbox.Map | null>(null);
  const [zones, setZones] = React.useState<Zone[]>([]);

  // Layer-selection step. `step` gates whether we show the picker or the zone
  // editor; `combined` carries the chosen layers/surface (undefined = legacy
  // single-season path driven by seasonId).
  const [step, setStep] = React.useState<"loading" | "select" | "zones">("loading");
  const [sources, setSources] = React.useState<TPrescriptionSources | null>(null);
  const [combined, setCombined] = React.useState<PrescriptionLayersOption | undefined>(undefined);

  // On mount, discover available layers and decide whether to show the picker.
  React.useEffect(() => {
    if (!isPaid) return; // Subscription paywall — show the buy prompt instead.
    let cancelled = false;
    getPrescriptionSources(field_id)
      .then((s) => {
        if (cancelled) return;
        setSources(s);
        const optionCount = s.yieldSeasons.length + (s.productivity.available ? 1 : 0);
        if (optionCount > 1) {
          // Multiple layers available — let the user choose.
          setStep("select");
          setLoading(false);
        } else if (optionCount === 1) {
          // Exactly one layer — skip the picker, use it directly.
          const only: PrescriptionLayersOption = s.productivity.available
            ? { layers: [{ type: "productivity" }], surface: "union" }
            : { layers: [{ type: "yield", seasonId: s.yieldSeasons[0].seasonId }], surface: "union" };
          setCombined(only);
          setStep("zones");
        } else {
          // Nothing stored — fall back to the legacy season path (likely empty).
          setStep("zones");
        }
      })
      .catch(() => {
        if (!cancelled) setStep("zones"); // graceful fallback to legacy
      });
    return () => {
      cancelled = true;
    };
  }, [field_id, isPaid]);

  // effect that loads prescription from MowerAPI every time count/layers change.
  React.useEffect(() => {
    if (step !== "zones") return;
    if (map === null) {
      console.warn("Map not loaded");
      return;
    }

    const source = map.getSource("prescription");
    if (!source) {
      console.warn("Cannot find prescription source");
      return;
    }

    setLoading(true);
    track(EVENTS.PRESCRIPTION_GENERATED, {
      zoneCount: count,
      interpolate,
      combined: Boolean(combined && combined.layers.length > 0),
    });
    fetchPrescription(field_id, count, interpolate, seasonId, combined).then((data) => {
      if (data.features.length === 0) {
        console.warn("no features in the mower response");
        return;
      }

      let zones = new Map<number, Zone>();

      // Calculate area for each zone
      data.features.forEach((feature) => {
        const zone_id: number = feature.properties!.zone;
        const area = turfArea(feature);
        if (!zones.has(zone_id)) {
          const zone = {
            id: zone_id,
            rate: 0,
            raw_rate: "",
            area: area,
            color: feature.properties!.fill as string,
          };
          zones.set(zone_id, zone);
        } else {
          let zone = zones.get(zone_id)!;
          zone.area += area;
          zones.set(zone_id, zone);
        }
      });

      setZones(Array.from(zones.values()));

      (source as GeoJSONSource).setData(data);

      const camera = map.cameraForBounds(
        turfBBox(data) as [number, number, number, number])!;
      map.flyTo({
        ...camera,
        essential: true,
        padding: { top: 10, bottom: 10, left: 400, right: 10 },
      });
    }).catch((e) => {
      // Backend allows one prescription per user at a time — inform, don't alarm.
      if (e?.code === "PRESCRIPTION_IN_PROGRESS") {
        toast.info(lang.prescription.busyPrescription);
        return;
      }
      console.error(e);
      toast.error("API request failed:" + e.toString());
    }).finally(() => {
      setLoading(false);
    });

  }, [field_id, count, map, seasonId, step, combined, lang]);

  if (!isPaid) {
    return (
      <StyleScreen>
        <PaywallLayout>
          <PaywallCard>
            <Text size={22} weight={500} textAlign="center" style={{ marginBottom: 8 }}>
              {lang.subscription.prescriptionTitle}
            </Text>
            <Text size={14} color="#6B7280" textAlign="center" style={{ marginBottom: 20 }}>
              {lang.subscription.prescriptionMessage}
            </Text>
            <Button kind="primary" onClick={() => navigate(-1)}>
              {lang.subscription.back}
            </Button>
          </PaywallCard>
        </PaywallLayout>
      </StyleScreen>
    );
  }

  return (
    <StyleScreen>
      {loading && (
        <LoadingLayout>
          <Spinner />
        </LoadingLayout>
      )}

      {step === "select" && sources && (
        <LayerPicker
          fieldId={field_id}
          sources={sources}
          onConfirm={(picked) => {
            setCombined(picked);
            setZones([]);
            setStep("zones");
          }}
          onCancel={() => navigate(-1)}
        />
      )}

      <GeoMap
        mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
        onLoad={(e) => {
          setMap(e.target);
        }}
      >
        <Source
          id="prescription"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: [],
          }}>
          <Layer
            type="fill"
            id="yield"
            paint={{
              "fill-color": ["get", "fill"],
              "fill-opacity": 0.8,
            }}
          />
        </Source>
      </GeoMap>
      {zones.length > 0 && (
        <>
          <StyleModalWindow>
            <StyleModalHeader
              onClick={() => {
                setHidden((prev) => {
                  return !prev;
                })
              }}
            >
              <StyleModalHeaderText>{lang.prescription.settings} {
                hidden
                  ? <ExpandMore style={{
                    verticalAlign: "middle",
                    display: "inline-block",
                  }} />
                  : <ExpandLess style={{
                    verticalAlign: "middle",
                    display: "inline-block",
                  }} />
              }</StyleModalHeaderText>
            </StyleModalHeader>
            <StyleModalContainer hidden={hidden}>
              <CountSelect countState={[count, setCount]} />
              <StandardRateInput
                value={rawStandard}
                onChange={(value) => {
                  setRawStandard(value);
                  const parsed = parseFloat(value);

                  if (_.isNumber(parsed) && _.isFinite(parsed)) {
                    setStandard(parsed);
                  }
                }} />
              <Options
                zones={zones}
                standard={standard}
                setRate={(id: number, rate: string) => {
                  setZones((prev) => {
                    return prev.map((zone) => {
                      if (zone.id === id) {
                        zone.raw_rate = rate;
                        const parsed = parseFloat(zone.raw_rate);
                        if (_.isNumber(parsed) && _.isFinite(parsed)) {
                          zone.rate = parsed;
                        }
                      }
                      return zone;
                    });
                  })
                }} />
            </StyleModalContainer>
          </StyleModalWindow>

          <StyleActions>
            <Button
              kind="primary"
              onClick={() => {
                setExportDialogVisible(true);
              }}
            >{lang.prescription.download}</Button>
            {jdConnected && (
              <Button
                kind="outline"
                onClick={handleUploadToDeere}
                style={{ backgroundColor: "#e8f0d8", borderColor: "#8aad3b" }}
              >
                {lang.johnDeere.uploadToFilesButton}
              </Button>
            )}
            <Button kind="outline" onClick={() => navigate(-1)}>
              {lang.prescription.cancel}
            </Button>
          </StyleActions>
          <DownloadDialog
            field_id={field_id}
            field_name={field_name}
            standard={standard}
            zones={zones}
            zoneCount={count}
            seasonId={seasonId}
            combined={combined}
            visible={isExportDialogVisible}
            onClose={() => {
              setExportDialogVisible(false);
            }}
          />
          <UploadToFilesDialog
            fieldId={field_id}
            fieldName={field_name}
            seasonId={seasonId}
            combined={combined}
            zones={zones}
            zoneCount={count}
            standard={standard}
            visible={isUploadFilesDialogVisible}
            onClose={() => setUploadFilesDialogVisible(false)}
          />
          <Dialog
            title="John Deere not connected"
            visible={connectPromptVisible}
            onClose={() => setConnectPromptVisible(false)}
            style={{ maxWidth: 400 }}
          >
            <Column crossAxisSize="max" style={{ gap: 12 }}>
              <Text size={14} color="#6B7280">
                You need to connect your John Deere account first. Go to Settings to set up the connection?
              </Text>
              <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
                <Button kind="outline" onClick={() => setConnectPromptVisible(false)}>
                  Cancel
                </Button>
                <Button onClick={() => { setConnectPromptVisible(false); navigate("/settings"); }}>
                  Go to Settings
                </Button>
              </Row>
            </Column>
          </Dialog>
        </>
      )}
    </StyleScreen>
  );
});

const Screen: React.FC = () => {
  return <Prescription />;
};

export default Screen;
